import joi from 'joi';

import {
  type PathLookupIpProtocol,
  type PathLookupTcpFlag,
  pathLookupIpProtocolSchema,
  pathLookupTcpFlagSchema,
} from '../pathLookup/PacketHeader';

const portSchema = joi
  .string()
  .regex(/^((\d+|\d+-|\d+-\d+|-\d+),)*(\d+|\d+-|\d+-\d+|-\d+)$/);

type PortsOptions = {
  /** string is comma separated list of ports or port ranges (eg. 34,42-,-56,12-655) */
  dstPorts: string;
  /** string is comma separated list of ports or port ranges (eg. 34,42-,-56,12-655) */
  srcPorts: string;
};
const portsOptionsSchema = joi.object().keys({
  dstPorts: portSchema.required(),
  srcPorts: portSchema.required(),
});

export type PathLookupTcpOptions = PortsOptions & {
  flags: PathLookupTcpFlag[];
};
export const pathLookupTcpOptionsSchema = portsOptionsSchema.keys({
  flags: joi.array().items(pathLookupTcpFlagSchema).required(),
});

export type PathLookupIcmpOptions = {
  type: number;
  code: number;
};
export const pathLookupIcmpOptionsSchema = joi.object().keys({
  type: joi.number().min(0).max(255).required(),
  code: joi.number().min(0).max(255).required(),
});

export type PathLookupUdpOptions = PortsOptions;
export const pathLookupUdpOptionsSchema = portsOptionsSchema;

export type PathLookupL4Options =
  | PathLookupTcpOptions
  | PathLookupIcmpOptions
  | PathLookupUdpOptions;

export function isPathLookupTcpL4Options(
  protocol: PathLookupIpProtocol,
  l4Options: PathLookupL4Options,
): l4Options is PathLookupTcpOptions {
  return protocol === 'tcp';
}
export function isPathLookupUdpL4Options(
  protocol: PathLookupIpProtocol,
  l4Options: PathLookupL4Options,
): l4Options is PathLookupUdpOptions {
  return protocol === 'udp';
}
export function isPathLookupIcmpL4Options(
  protocol: PathLookupIpProtocol,
  l4Options: PathLookupL4Options,
): l4Options is PathLookupIcmpOptions {
  return protocol === 'icmp' || protocol === 'icmpv6';
}

export type PathLookupOtherOptions = {
  applications: string;
  applicationsMatchWithRegex: boolean;
  category: string;
  tracked: boolean;
  url: string;
};
export const pathLookupOtherOptionsSchema = joi.object().keys({
  applications: joi.string().allow('').required(),
  applicationsMatchWithRegex: joi.boolean().default(true),
  category: joi.string().allow('').default(''), // optional
  tracked: joi.boolean().required(),
  url: joi.string().allow('').default(''), // optional
});

export type PathLookupAdvancedOptionsBase = {
  dstRegions: string;
  enableRegions: boolean;
  fragmentOffset: number;
  otherOptions: PathLookupOtherOptions;
  protocol: PathLookupIpProtocol;
  srcRegions: string;
  ttl: number;
};

export type PathLookupAdvancedOptionsTcp = PathLookupAdvancedOptionsBase & {
  l4Options: PathLookupTcpOptions;
};
export type PathLookupAdvancedOptionsUdp = PathLookupAdvancedOptionsBase & {
  l4Options: PathLookupUdpOptions;
};
export type PathLookupAdvancedOptionsIcmp = PathLookupAdvancedOptionsBase & {
  l4Options: PathLookupIcmpOptions;
};

export type PathLookupAdvancedOptions =
  | PathLookupAdvancedOptionsTcp
  | PathLookupAdvancedOptionsUdp
  | PathLookupAdvancedOptionsIcmp;

export const pathLookupAdvancedOptionsSchema = joi.object().keys({
  protocol: pathLookupIpProtocolSchema.required(),
  l4Options: joi
    .when('protocol', {
      is: 'tcp',
      then: pathLookupTcpOptionsSchema.required(),
    })
    .when('protocol', {
      is: 'icmp',
      then: pathLookupIcmpOptionsSchema.required(),
    })
    .when('protocol', {
      is: 'icmpv6',
      then: pathLookupIcmpOptionsSchema.required(),
    })
    .when('protocol', {
      is: 'udp',
      then: pathLookupUdpOptionsSchema.required(),
    }),
  enableRegions: joi.boolean().required(),
  srcRegions: joi.string().allow('').required(),
  dstRegions: joi.string().allow('').required(),
  ttl: joi.number().min(1).max(256).required(),
  fragmentOffset: joi
    .number()
    .min(0)
    .max((1 << 13) - 1)
    .required(),
  otherOptions: pathLookupOtherOptionsSchema.required(),
});

export type NamedPathLookupAdvancedOptions = PathLookupAdvancedOptions & {
  id: string;
  name: string;
  factory?: boolean;
};

export const namedPathLookupAdvancedOptionsSchema =
  pathLookupAdvancedOptionsSchema.keys({
    id: joi.string(), // not required, e.g. for POST / DELETE operations
    name: joi.string().required(),
    factory: joi.boolean(),
  });
